<template>
  <div class="full-width full-height">
    <div style="display: flex">
      <el-container name="patient-list" class="split-right half-width">
        <el-header :style="`height: ${headerH}px`">
          <div class="header-container2">
            <el-input
              style="width: 300px; font-size: 25px"
              :placeholder="$t('langcfg.txtNameOrPID')"
              v-model.lazy="namePid"
              @click.native.prevent.stop="handleClick"
              @touchend.native.stop.prevent="handleClick"
            >
            </el-input>
            <div>
              <el-date-picker
                v-model="dateRange"
                @change="dateRangeChanged"
                class="datePicker"
                type="daterange"
                :picker-options="pickerOptions"
                range-separator="-"
                :start-placeholder="`${$t('langcfg.txtStartDate')}`"
                :end-placeholder="`${$t('langcfg.txtEndDate')}`"
                clear-icon="el-icon-circle-close"
              >
              </el-date-picker>
            </div>
            <div style="display: flex; align-items: center">
              <input
                type="checkbox"
                style="width: 35px; height: 35px"
                @change="selectAll"
                v-model="isAllSelected"
              />
              <span :style="`${isDE ? 'font-size: 22px' : 'font-size: 25px'}`"
                >&nbsp;{{ $t("langcfg.txtSelectAllPatients") }}</span
              >
            </div>
          </div>
        </el-header>
        <el-main :style="`height:${tableHeight}px;padding:10px`">
          <el-table
            ref="patientTable"
            :data="patients"
            style="width: 100%"
            :row-style="rowCtyle"
            row-key="_id"
            highlight-current-row
            :header-row-style="headerStyle"
            :needSelectFirstRow="true"
            @current-change="currentPatientChanged"
            @select="patientSelectChanged"
            @select-all="patientSelectionChanged"
          >
            <span style="color: rgba(255, 255, 255, 0.2)" slot="empty">{{
              $t("langcfg.txtNoData")
            }}</span>
            <el-table-column type="selection" align="center" style="width: 50px"> </el-table-column>
            <el-table-column :label="$t('langcfg.txtName')" align="center" prop="firstname">
              <template slot-scope="scope">{{
                scope.row.lastname
                  ? `${scope.row.firstname} ${scope.row.lastname}`
                  : scope.row.firstname
              }}</template>
            </el-table-column>
            <el-table-column label="PID" align="center" prop="pid">
              <template slot-scope="scope">{{ scope.row.pid }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('langcfg.txtCreationDate')"
              align="center"
              prop="creationdate"
            >
              <template slot-scope="scope">{{ getDate(scope.row.creationdate) }}</template>
            </el-table-column>
            <el-table-column :label="$t('langcfg.txtSex')" align="center" width="130" prop="sex">
              <template slot-scope="scope">{{ getSex(scope.row.sex) }}</template>
            </el-table-column>
          </el-table>
        </el-main>
        <el-footer :style="`height: ${headerHeight}px`">
          <el-pagination
            layout="prev, pager, next"
            :page-size="pagination.pageSize"
            :total="pagination.total"
            :current-page="pagination.page"
            :page-count="pagination.pageCount"
            :background="true"
            @current-change="currentPageChanged"
          ></el-pagination>
        </el-footer>
      </el-container>
      <el-container name="record-list" class="half-width">
        <el-header :style="`height: ${headerH}px`">
          <div class="header-container2">
            <span style="font-size: 25px">{{ $t("langcfg.txtCheckType") }}:&nbsp;&nbsp;</span>
            <el-select
              v-model="currentCheckType"
              id="select"
              @change="checkTypeChanged"
              :disabled="!isTopographyEnabled || !isDryEyeEnabled"
            >
              <el-option
                v-for="item in checkTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              >
              </el-option>
            </el-select>
          </div>
        </el-header>
        <el-container>
          <el-main :style="`height:${tableHeight}px`">
            <el-main :style="`height:${tableHeight}px;padding:10px`">
              <el-table
                ref="recordTable"
                :data="records"
                row-key="_id"
                style="width: 100%"
                :row-style="rowCtyle"
                highlight-current-row
                :header-row-style="headerStyle"
                :needSelectFirstRow="true"
                @select="recordSelectChanged"
                @select-all="recordSelectionChanged"
              >
                <span style="color: rgba(255, 255, 255, 0.2)" slot="empty">{{
                  $t("langcfg.txtNoData")
                }}</span>
                <el-table-column type="selection" align="center"> </el-table-column>

                <el-table-column :label="$t('langcfg.txtCheckTime')" align="center">
                  <template slot-scope="scope">{{ getDate(scope.row.testtime) }}</template>
                </el-table-column>
                <el-table-column :label="$t('langcfg.txtCheckType')" align="center">
                  <template slot-scope="scope">{{ getCheckType(scope.row.checktype) }}</template>
                </el-table-column>
                <el-table-column :label="$t('langcfg.txtEye')" align="center">
                  <template slot-scope="scope">{{ (scope.row.eyetype || []).join(",") }}</template>
                </el-table-column>
              </el-table>
            </el-main></el-main
          >
        </el-container>
        <el-footer :style="`height: ${headerHeight}px`">
          <div style="display: flex; justify-content: space-around">
            <el-button @click="exportToUdisk">{{ $t("langcfg.txtExportToUdisk") }}</el-button>
            <el-button @click="exportToSharedFolder">{{
              $t("langcfg.txtExportToSharedFolder")
            }}</el-button>
            <el-button @click="showExportOptions">{{ $t("langcfg.txtExportOptions") }}</el-button>
            <el-button type="primary" @click="goBackToPatientList">{{
              $t("langcfg.txtBack")
            }}</el-button>
          </div>
        </el-footer>
      </el-container>
    </div>
    <export-options :dialogVisible.sync="dialogVisible" v-if="dialogVisible"></export-options>
    <div class="export-wait" v-if="isExporting">
      <div style="height: 5%">
        <span
          @click="stopExport"
          style="font-size: 50px; position: absolute; left: 95%"
          class="el-icon-close"
        ></span>
      </div>
      <div
        style="
          display: flex;
          height: 90%;
          align-items: center;
          justify-content: center;

          flex-direction: column;
        "
      >
        <div>
          {{ $t("langcfg.txtExporting") }}...
          <span id="export-tip"> 0% </span>
        </div>
        <!-- <div>{{ $t('langcfg.txtExporting') }}: {{ recName }}</div> -->
      </div>
    </div>
  </div>
</template>
<script>
import moment from "moment";
import EVENTS from "../../../bus";
import { Size, Message, License } from "../../../mixins";
import { getAllPatients, getPatients } from "../../../services/patient";
import { getAllRecordsOfPatientWithoutContent } from "../../../services/record";
import ExportOptions from "./ExportOptions.vue";
import ExportTopoAPI from "./exportTopoApi";
import ExportDryEyeApi from "./exportDryEyeApi";
import fileSys from "../../../fileoprators/fileSystem";
import { getSystemTime, isDefined, json2string, logStdout } from "../../../shared/utils";
import { compressFile, copy2Udisk } from "./funcs";
let exportApi;
let exportEyeApi;
export default {
  name: "ExportData",
  data() {
    return {
      pagination: {
        page: 1,
        pageSize: 15,
        total: 150,
        pageCount: 10
      },
      dialogVisible: false,
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t("langcfg.txtLastWeek"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
              picker.$emit("pick", [start, end]);
            }
          },
          {
            text: this.$t("langcfg.txtLastMonth"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
              picker.$emit("pick", [start, end]);
            }
          },
          {
            text: this.$t("langcfg.txtLastThreeMonths"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
              picker.$emit("pick", [start, end]);
            }
          },
          {
            text: this.$t("langcfg.txtClear"),
            onClick(picker) {
              picker.$emit("pick", []);
            }
          }
        ]
      },
      patients: [],
      dateRange: [],
      namePid: "",
      checkTypeOptions: [],
      currentCheckType: "",
      selectedPatients: {},
      unSelectedPatients: {},
      selectedRecords: {},
      unSelectedRecords: {},
      isAllSelected: false,
      records: [],
      currentPatient: undefined,
      isExporting: false,
      epxortConunt: 0,
      recName: "",
      isCompress: false,
      isDE: false
    };
  },
  mounted() {
    if (this.isTopographyEnabled && this.isDryEyeEnabled) {
      this.checkTypeOptions = [
        {
          value: "dryeye",
          label: this.$t("langcfg.txtDryEye")
        },
        {
          value: "topography",
          label: this.$t("langcfg.txtTopography")
        },
        {
          value: "",
          label: this.$t("langcfg.txtAll")
        }
      ];
    } else {
      if (this.isTopographyEnabled) {
        this.currentCheckType = "topography";
        this.checkTypeOptions = [
          {
            value: "topography",
            label: this.$t("langcfg.txtTopography")
          }
        ];
      } else {
        this.currentCheckType = "dryeye";
        this.checkTypeOptions = [
          {
            value: "dryeye",
            label: this.$t("langcfg.txtDryEye")
          }
        ];
      }
    }
    this.pagination.pageSize = window.copyScreen ? 12 : 14;
    this.selectedPatients = {};
    this.unSelectedPatients = {};
    this.isCompress = false;
    this.getPatients();
    this.isDE = window.settings.language === "de";
  },
  updated() {
    this.isDE = window.settings.language === "de";
  },
  beforeDestroy() {
    exportApi = null;
    exportEyeApi = null;
    this.patients = null;
    this.records = null;
  },
  destroyed() {
    this.$destroy();
  },
  components: {
    ExportOptions
  },
  computed: {
    currentPageKey: function () {
      return `page${this.pagination.page}`;
    },
    tableHeight: function () {
      return this.dispH - (this.headerH + this.headerF) - 10;
    },
    headerH: function () {
      return this.headerHeight + 10;
    },
    headerF: function () {
      return this.headerHeight + 5;
    }
  },
  mixins: [Size, Message, License],
  methods: {
    showExportOptions() {
      this.dialogVisible = true;
    },
    getExportPatientRecords(callback) {
      const { page, pageSize } = this.pagination;
      let opt = {
        page: 1,
        pagelength: pageSize,
        sort: {
          key: "creationdate",
          asc_desc: -1
        },
        search: {
          $or: [
            {
              firstname: this.namePid
            },
            {
              pid: this.namePid
            },
            {
              lastname: this.namePid
            }
          ]
        }
      };
      getAllPatients(opt, [], (err, patients) => {
        if (err) {
          this.message(this.$t("langcfg.txtFailedToGetPatient"), "error");
        } else {
          let pats = patients.filter((pat) => {
            return this.isAllSelected
              ? !this.unSelectedPatients[pat._id]
              : this.selectedPatients[pat._id];
          });
          this.countRecords(pats.concat(), 0, (count) => {
            if (callback) callback(pats, count);
          });
        }
      });
    },
    countRecords(pats, count = 0, callback) {
      if (pats.length <= 0) {
        if (callback) callback(count);
      } else {
        const pat = pats.pop();
        getAllRecordsOfPatientWithoutContent(
          pat._id,
          {
            checktype: this.currentCheckType
          },
          (res) => {
            if (res.message === "OK") {
              count += res.records.length;
              this.countRecords(pats, count, callback);
            } else {
              this.countRecords(pats, count, callback);
            }
          }
        );
      }
    },
    copyToUdisk(src, callback) {
      const dst = `${src}.tar`;
      compressFile(src, dst, (err) => {
        if (err) {
          this.$bus.$emit(EVENTS.canLogout, true);
          this.message(this.$t("langcfg.txtExportFailed"), "error");
          if (callback) callback(err);
        } else {
          const { location } = fileSys.isUDiskExits();
          copy2Udisk(dst, location, (err) => {
            this.$bus.$emit(EVENTS.canLogout, true);
            if (err) {
              if ((err.message || "").indexOf("space not enough") >= 0) {
                this.message(this.$t("langcfg.txOutOfMemory"), "error");
              } else {
                this.message(this.$t("langcfg.txtExportFailed"), "error");
              }
              if (callback) callback(new Error(""));
            } else {
              if (callback) callback();
            }
          });
        }
      });
    },
    initExportParams(exporting, err) {
      fileSys.clearCache(() => {
        this.$bus.$emit(EVENTS.canLogout, true);
        this.isExporting = false;
        this.epxortConunt = 0;
        this.recName = "";
        if (exporting && !err) {
          this.message(this.$t("langcfg.txtExportSuccess"));
        }
      });
    },
    exportTopography(storage, patients, count, callback) {
      if (!this.isExportOptionsValid().topography) {
        if (callback) callback();
        return;
      }
      const dataShowSettings = window.dataShowSettings;
      const langcfg = this.$i18n.messages[window.settings["language"]].langcfg;
      exportApi = new ExportTopoAPI(
        storage,
        { ...window.exportOptionSettings, langcfg, dataShowSettings },
        patients.concat(),
        this.unSelectedRecords
      );
      exportApi.on("export-count", (recName) => {
        let percent = parseInt((++this.epxortConunt / count) * 100) + "%";
        this.recName = recName;
        if (document.getElementById("export-tip"))
          document.getElementById("export-tip").innerHTML = percent;
      });
      exportApi.exportPatients((exporting) => {
        if (this.isExportOptionsValid().dryeye) {
          if (callback) {
            callback();
          }
        } else {
          if (document.getElementById("export-tip"))
            document.getElementById("export-tip").innerHTML = "100%";
          if (this.isCompress) {
            setTimeout(() => {
              this.copyToUdisk(storage, (err) => {
                this.initExportParams(exporting, err);
              });
            }, 1000);
          } else {
            this.initExportParams(exporting);
          }
        }
      });
    },
    exportDryeye(storage, patients, count) {
      const dataShowSettings = window.dataShowSettings;
      const langcfg = this.$i18n.messages[window.settings["language"]].langcfg;
      exportEyeApi = new ExportDryEyeApi(
        storage,
        { ...window.exportOptionSettings, langcfg, dataShowSettings },
        patients.concat(),
        this.unSelectedRecords
      );
      exportEyeApi.on("export-count", (recName) => {
        let percent = parseInt((++this.epxortConunt / count) * 100) + "%";
        this.recName = recName;
        if (document.getElementById("export-tip"))
          document.getElementById("export-tip").innerHTML = percent;
      });
      exportEyeApi.exportPatients((exporting) => {
        if (document.getElementById("export-tip"))
          document.getElementById("export-tip").innerHTML = "100%";
        if (this.isCompress) {
          setTimeout(() => {
            this.copyToUdisk(storage, (err) => {
              this.initExportParams(exporting, err);
            });
          }, 1000);
        } else {
          this.initExportParams(exporting);
        }
      });
    },
    startExportDryTopography(storage) {
      this.epxortConunt = 0;
      if (!this.isExportOptionsValid().hasone) {
        this.message(this.$t("langcfg.txtConfigureExportSettings"), "info");
        return;
      }
      this.getExportPatientRecords((patients, count) => {
        if (count === 0) {
          this.message(this.$t("langcfg.txtSelectPatient"), "info");
          this.isExporting = false;
          return;
        }
        this.isExporting = true;
        this.$bus.$emit(EVENTS.canLogout, false);
        this.exportTopography(storage, patients, count, () => {
          this.exportDryeye(storage, patients, count);
        });
      });
    },
    isExportOptionsValid() {
      const { dryeye, topography } = window.exportOptionSettings.exportOptions;
      const dryTopo = { dryeye: false, topography: false, all: false, hasone: false };
      for (let id in dryeye.children) {
        if (dryeye.children[id]) {
          dryTopo.dryeye = true;
          break;
        }
      }
      for (let idx in topography.children) {
        if (topography.children[idx]) {
          dryTopo.topography = true;
          break;
        }
      }
      dryTopo.dryeye =
        dryTopo.dryeye && (this.currentCheckType === "" || this.currentCheckType === "dryeye");
      dryTopo.topography =
        dryTopo.topography &&
        (this.currentCheckType === "" || this.currentCheckType === "topography");
      dryTopo.hasone = dryTopo.dryeye || dryTopo.topography;
      return dryTopo;
    },
    exportToUdisk() {
      this.isCompress = false;
      const { status, location } = fileSys.isUDiskExits();
      if (!status) {
        this.message(this.$t("langcfg.txtDiskIsMountedFailed"), "error", 10000);
        return;
      }
      const dt = getSystemTime("", "_", "");
      this.startExportDryTopography(fileSys.combinePath(location, `DEA520Data_${dt}`));
    },
    exportToSharedFolder() {
      this.isCompress = false;
      let { ip, folder, username, password, folderInLinux } = window.settings.sharedFolder;
      logStdout("shared folder info:", window.settings.sharedFolder);
      fileSys.mountSharedFS([`${ip}/${folder}`, `${username}`, `${password}`], (error) => {
        if (error) {
          this.message(this.$t("langcfg.txtSharedFolderMountFailed"), "error", 2000);
        } else {
          this.startExportDryTopography(folderInLinux);
        }
      });
    },
    headerStyle() {
      return {
        fontSize: "24px",
        background: "transparent",
        height: "50px"
      };
    },
    stopExport() {
      if (exportApi) {
        exportApi.stopExport();
      }
      if (exportEyeApi) {
        exportEyeApi.stopExport();
      }
      fileSys.clearCache(() => {
        this.isExporting = false;
        this.epxortConunt = 0;
      });
    },
    selectAll() {
      for (let id in this.patients) {
        this.$refs.patientTable.toggleRowSelection(this.patients[id], this.isAllSelected);
      }
      for (let id in this.records) {
        this.$refs.recordTable.toggleRowSelection(this.records[id], this.isAllSelected);
      }
      this.selectedPatients = {};
      this.unSelectedPatients = {};
      this.selectedRecords = {};
      this.unSelectedRecords = {};
    },

    procSelectedPatients(rows, row) {
      this.patients.forEach((pat) => {
        if (
          rows.filter((item) => {
            return item._id === pat._id;
          }).length === 0
        ) {
          this.selectedPatients[pat._id] = false;
          this.unSelectedPatients[pat._id] = true;
        } else {
          this.selectedPatients[pat._id] = true;
          this.unSelectedPatients[pat._id] = false;
        }
        if (this.currentPatient._id === pat._id) {
          this.getRecords(this.currentPatient._id, () => {
            this.$nextTick(() => {
              this.records.forEach((rec) => {
                this.selectedRecords[rec._id] = this.selectedPatients[pat._id];
                this.unSelectedRecords[rec._id] = !this.selectedRecords[rec._id];
                this.$refs.recordTable.toggleRowSelection(rec, this.selectedRecords[rec._id]);
              });
            });
          });
        }
      });
    },
    procSelectedRecords(rows, row) {
      this.records.forEach((rec) => {
        if (
          rows.filter((item) => {
            return item._id === rec._id;
          }).length === 0
        ) {
          this.selectedRecords[rec._id] = false;
          this.unSelectedRecords[rec._id] = true;
        } else {
          this.selectedRecords[rec._id] = true;
          this.unSelectedRecords[rec._id] = false;
        }
      });
      if (rows.length === 0) {
        this.$refs.patientTable.toggleRowSelection(this.currentPatient, false);
        this.unSelectedPatients[this.currentPatient._id] = true;
        this.selectedPatients[this.currentPatient._id] = false;
      } else {
        this.selectedPatients[this.currentPatient._id] = true;
        this.unSelectedPatients[this.currentPatient._id] = false;
        this.$refs.patientTable.toggleRowSelection(this.currentPatient, true);
      }
    },
    patientSelectionChanged(rows) {
      this.procSelectedPatients(rows);
    },
    patientSelectChanged(rows, row) {
      this.currentPatient = row;
      this.procSelectedPatients(rows);
    },

    recordSelectChanged(rows, row) {
      this.procSelectedRecords(rows);
    },
    recordSelectionChanged(rows) {
      this.procSelectedRecords(rows);
    },
    getRecords(patientid, callback) {
      getAllRecordsOfPatientWithoutContent(
        patientid,
        {
          checktype: this.currentCheckType
        },
        (res) => {
          if (res.message === "OK") {
            this.records = res.records.concat();
            if (callback) callback();
          } else {
            if (callback) callback(res.error);
          }
        }
      );
    },
    rowCtyle() {
      return {
        fontSize: "25px",
        background: "transparent",
        height: "60px"
      };
    },
    goBackToPatientList() {
      this.$emit("close-export");
    },
    getPatients(callback) {
      this.currentPatient = this.patients[0];
      const { page, pageSize } = this.pagination;
      let opt = {
        page: page,
        pagelength: pageSize,
        sort: {
          creationdate: -1
        },
        search: {
          $or: [
            {
              firstname: this.namePid
            },
            {
              pid: this.namePid
            },
            {
              lastname: this.namePid
            }
          ]
        }
      };
      if (this.dateRange && this.dateRange.length === 2) {
        opt.dateRange = [
          this.dateRange[0].format("YYYY-MM-DD"),
          this.dateRange[1].format("YYYY-MM-DD")
        ];
      }
      getPatients(opt, (res) => {
        if (res.message === "OK") {
          this.pagination.total = res.allPatientsCount;
          this.patients = res.patients.concat();
          this.currentPatient = this.patients[0];
          this.$nextTick(() => {
            this.$refs.patientTable.setCurrentRow(this.patients[0]);
          });
          if (callback) callback();
        }
      });
    },
    currentPatientChanged(row) {
      this.currentPatient = row;
      this.getRecords(row._id, () => {
        this.$nextTick(() => {
          this.records.forEach((rec) => {
            if (!isDefined(this.unSelectedRecords[rec._id]) || !this.unSelectedRecords[rec._id]) {
              this.selectedRecords[rec._id] =
                this.isAllSelected || !!this.selectedPatients[row._id];
              this.unSelectedRecords[rec._id] = !this.selectedRecords[rec._id];
            } else {
              this.selectedRecords[rec._id] = false;
              this.unSelectedRecords[rec._id] = !this.selectedRecords[rec._id];
            }
            this.$refs.recordTable.toggleRowSelection(rec, this.selectedRecords[rec._id]);
          });
        });
      });
    },
    currentPageChanged(page) {
      this.pagination.page = page;
      this.getPatients(() => {
        this.$nextTick(() => {
          this.patients.forEach((pat) => {
            if (!isDefined(this.unSelectedPatients[pat._id]) || !this.unSelectedPatients[pat._id]) {
              this.selectedPatients[pat._id] =
                this.isAllSelected || !!this.selectedPatients[pat._id];
              this.unSelectedPatients[pat._id] = !this.selectedPatients[pat._id];
              this.$refs.patientTable.toggleRowSelection(pat, this.selectedPatients[pat._id]);
            } else {
              this.selectedPatients[pat._id] = false;
              this.$refs.patientTable.toggleRowSelection(pat, false);
            }
          });
        });
      });
    },
    searchInputChanged() {
      this.pagination.page = 1;
      this.getPatients();
    },
    handleClick() {
      let _this = this;
      this.$bus.$emit(EVENTS.spreadKeyBoard, this.namePid, (value) => {
        _this.namePid = value;
        _this.getPatients();
      });
    },
    getDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm");
    },
    getSex(sex) {
      switch (sex) {
        case "F":
          return this.$t("langcfg.txtFemale");
        case "M":
          return this.$t("langcfg.txtMale");
        case "O":
          return this.$t("langcfg.txtSexOther");
        case "U":
          return this.$t("langcfg.txtSexUnknown");
        default:
          return "";
      }
    },
    getCheckType(checktype) {
      return checktype === "dryeye"
        ? this.$t("langcfg.txtDryEye")
        : this.$t("langcfg.txtTopography");
    },
    dateRangeChanged(ev) {
      this.getPatients();
    },
    checkTypeChanged() {
      this.currentPatientChanged(this.currentPatient);
    }
  }
};
</script>
<style lang="less" scoped>
.el-pagination {
  display: flex;
  justify-content: center;
}
.el-pagination /deep/ li {
  width: 80px;
  height: 60px;
  font-size: 35px;
  line-height: 60px;
}
/deep/ .el-input__inner {
  font-size: 25px;
  .el-input__icon {
    font-size: 25px;
  }
}
/deep/ .el-range-input {
  font-size: 25px;
}
.el-pagination /deep/ .el-pager li {
  background-color: @page-background;
  color: white !important;
  border: 1px solid gray;
}
.el-pagination /deep/ button {
  width: 80px;
  height: 60px;
  i::before {
    font-size: 40px;
  }
}
.el-pagination /deep/ .btn-next,
.el-pagination /deep/ .btn-prev {
  background-color: @page-background;
  color: white !important;
  border: 1px solid gray;
}
.el-pagination /deep/ .el-pagination__jump {
  color: white;
  font-size: 4px;
}

.split-right {
  border-right: 0.2px solid gray;
}
.header-container2 {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 100%;
}
.el-header {
  border-bottom: 0.2px solid gray;
}
/deep/ .el-checkbox__inner {
  width: 20px;
  height: 20px;
}
/deep/ .el-checkbox__inner::after {
  width: 10px;
  height: 15px;
  top: -3px;
}
.el-button {
  background: transparent;
  border: 1px solid gray;
  height: 60px;
  min-width: 125px;
  font-size: 20px;
  padding: 10px;
}
.export-wait {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  z-index: 100;
  background: rgba(0, 0, 0, 0.7);
  font-size: 40px;
}
/deep/ .el-checkbox__inner {
  width: 35px;
  height: 35px;
}
/deep/ .el-checkbox__inner {
  width: 35px;
  height: 35px;
}
/deep/ .el-checkbox__inner::after {
  left: 10px;
  top: 5px;
}
</style>



// WEBPACK FOOTER //
// src/mainInterface/components/exportData/index.vue